import type { InputHTMLAttributes } from 'react';
import './Switch.css';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Hides the visible label text but keeps it for assistive tech — for the rare case a row's
   * own text already states what the switch does (DESIGN.md still wants an accessible name).
   * MVP-264: this is now true. Before, it deleted the name. */
  hideLabel?: boolean;
}

/** DESIGN.md §9.1 `Switch`. Used throughout Settings (AC-OPS-10) for every boolean field. */
export function Switch({ checked, onChange, label, hideLabel = false, disabled, className, ...rest }: SwitchProps) {
  return (
    <label className={['switch', disabled ? 'switch--disabled' : '', className].filter(Boolean).join(' ')}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.currentTarget.checked)}
        {...rest}
      />
      <span className="switch__track" aria-hidden="true">
        <span className="switch__thumb" />
      </span>
      {/* MVP-264: `hideLabel` used to drop the label text outright, leaving this `<label>` holding
          nothing but `aria-hidden` decoration — so the switch had no accessible name at all, the
          opposite of what the prop's own doc comment above promises. Measured on the shipped build:
          all three Cockpit rotation pause/resume switches announced as a bare "switch, on", with no
          way to tell whose turn was about to be paused. Visually hidden, not removed. */}
      <span className={hideLabel ? 'visually-hidden' : 'text-body'}>{label}</span>
    </label>
  );
}
